import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = { class: "booking-card" }
const _hoisted_2 = { class: "row g-3 align-items-stretch" }
const _hoisted_3 = { class: "col-12 col-md-6" }
const _hoisted_4 = { class: "col-12 col-md-6" }
const _hoisted_5 = { class: "card-body d-flex flex-column pt-0 pb-0 h-100 px-2 px-md-4" }
const _hoisted_6 = {
  key: 2,
  class: "mt-auto"
}
const _hoisted_7 = { class: "d-flex gap-2 flex-column flex-md-row" }
const _hoisted_8 = {
  key: 3,
  class: "mt-auto"
}

import { computed, ComputedRef } from 'vue'

  /* ------------------- Store ------------------- */
  import { useStore } from '@/store'

  /* ------------------- Configurations & Enums ------------------- */
  import { ModalId } from '@/config/modalIds'

  /* ------------------- Types ------------------- */
  import {
    BookingSummary,
    TravellerSummary,
  } from '@/services/BookingService.interface'

  /* ------------------- Components ------------------- */
  import BookingImage from '@/components/common/BookingImage.vue'
  import BookingHeader from '@/components/common/BookingHeader.vue'
  import BookingDate from '@/components/common/BookingDate.vue'
  import PrestationInfo from '@/components/common/PrestationInfo.vue'
  import PackageContainer from '@/components/common/PackageContainer.vue'
  import MissingTravellerInfoWarning from '@/components/common/MissingTravellerInfoWarning.vue'
  import PaymentButton from '@/components/common/PaymentButton.vue'
  import ViewDetailsButton from '@/components/common/ViewDetailsButton.vue'
  import IresaReference from '@/components/common/IresaReference.vue'
  import PanelAction from '@/components/common/PanelAction.vue'
  import PriceAndViewDetailsButton from '@/components/common/PriceAndViewDetailsButton.vue'
  import ReserveActionsButton from '@/components/common/ReserveActionsButton.vue'

  /* ------------------- Composables ------------------- */
  import { useBookingStatus } from '@/composables/useBookingStatus'
  import { useSectionVisibilty } from '@/composables/useSectionVisibilty'
  import { useBookingFunnelActions } from '@/composables/useBookingFunnelActions'
  import { useBookingFunnelNavigation } from '@/composables/useBookingFunnelNavigation'
  /* ------------------- Store & Props ------------------- */
  
export default /*@__PURE__*/_defineComponent({
  __name: 'Booking',
  props: {
    booking: {},
    isPanelView: { type: Boolean }
  },
  emits: ["view-details"],
  setup(__props: any) {

  /* ------------------- Vue Core & Utilities ------------------- */
  const store = useStore()

  const props = __props

  /* ------------------- Section visibility ------------------- */
  const {showStationHeader, showBookingDate, showPrestationInfo, showPanelAction, showPackageSection} = useSectionVisibilty(props.booking.liheId, props.booking.liheName, props.booking.packageCode)

  /* ------------------- Booking Statuses ------------------- */
  const { isReservationStatus, isQuoteOrCartInProgress, isPastStatus } =
    useBookingStatus(props.booking)

  const { showPayBalanceButton } = useBookingFunnelActions(() => props.booking)
  const { payActionLabel } = useBookingFunnelNavigation(() => props.booking)

  /* ------------------- Computed Properties ------------------- */
  /**
   * Retrieves the user ID from the store.
   */
  const userId: ComputedRef<string | null> = computed(
    () => store.state.auth?.user?.iresa_id || null
  )

  /**
   * Checks if any traveller is missing required information.
   */
  const missingTravellerInfo: ComputedRef<boolean> = computed(() =>
    props.booking.travellers.some(
      (traveller: TravellerSummary) =>
        !traveller.firstName ||
        traveller.firstName.trim() === '' ||
        !traveller.lastName ||
        traveller.lastName.trim() === '' ||
        traveller.firstName === null ||
        traveller.lastName === null
    )
  )

  /* ------------------- Emits ------------------- */
  

  /* ------------------- Methods ------------------- */
  /**
   * Handles the "View Details" action by opening the booking panel modal.
   */
  const handleViewDetails = (): void => {
    store.commit('modal/open', ModalId.BOOKING_PANEL)
    store.dispatch('booking/selectBooking', props.booking)
  }

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _createVNode(BookingImage, {
          image: _ctx.booking.image,
          class: "h-100"
        }, null, 8, ["image"])
      ]),
      _createElementVNode("div", _hoisted_4, [
        _createElementVNode("div", _hoisted_5, [
          _createVNode(BookingHeader, {
            liheName: _ctx.booking.liheName,
            stationName: _ctx.booking.stationName,
            "show-station-header": _unref(showStationHeader)
          }, null, 8, ["liheName", "stationName", "show-station-header"]),
          (_unref(showBookingDate))
            ? (_openBlock(), _createBlock(BookingDate, {
                key: 0,
                class: "mt-2",
                "booking-option": _ctx.booking.options.find(option => option.prestTypeId === 1) || {
              startDate: _ctx.booking.departureDate,
              endDate: _ctx.booking.returnDate,
              prestTypeId: 1
            }
              }, null, 8, ["booking-option"]))
            : _createCommentVNode("", true),
          (_unref(showPrestationInfo))
            ? (_openBlock(), _createBlock(PrestationInfo, {
                key: 1,
                options: _ctx.booking.options
              }, null, 8, ["options"]))
            : _createCommentVNode("", true),
          _createVNode(IresaReference, {
            reference: _ctx.booking.reference
          }, null, 8, ["reference"]),
          (!_ctx.isPanelView && _unref(isReservationStatus))
            ? (_openBlock(), _createElementBlock("div", _hoisted_6, [
                (missingTravellerInfo.value)
                  ? (_openBlock(), _createBlock(MissingTravellerInfoWarning, {
                      key: 0,
                      booking: _ctx.booking
                    }, null, 8, ["booking"]))
                  : _createCommentVNode("", true),
                _createElementVNode("div", _hoisted_7, [
                  _createVNode(ViewDetailsButton, {
                    booking: _ctx.booking,
                    onClick: handleViewDetails
                  }, null, 8, ["booking"]),
                  (_unref(showPayBalanceButton))
                    ? (_openBlock(), _createBlock(ReserveActionsButton, {
                        key: 0,
                        booking: _ctx.booking,
                        "default-label": _unref(payActionLabel),
                        class: "flex-grow-1"
                      }, null, 8, ["booking", "default-label"]))
                    : (_openBlock(), _createBlock(PaymentButton, {
                        key: 1,
                        userId: userId.value,
                        booking: _ctx.booking,
                        class: "flex-grow-1"
                      }, null, 8, ["userId", "booking"]))
                ])
              ]))
            : _createCommentVNode("", true),
          (!_ctx.isPanelView && _unref(isQuoteOrCartInProgress))
            ? (_openBlock(), _createElementBlock("div", _hoisted_8, [
                (_unref(showPackageSection))
                  ? (_openBlock(), _createBlock(PackageContainer, {
                      key: 0,
                      packageCode: _ctx.booking.packageCode,
                      bookingReference: _ctx.booking.reference || ''
                    }, null, 8, ["packageCode", "bookingReference"]))
                  : _createCommentVNode("", true),
                _createVNode(PriceAndViewDetailsButton, {
                  booking: _ctx.booking,
                  "is-reservation": false
                }, null, 8, ["booking"])
              ]))
            : _createCommentVNode("", true),
          (_ctx.isPanelView && _unref(showPanelAction))
            ? (_openBlock(), _createBlock(PanelAction, {
                key: 4,
                isPastStatus: _unref(isPastStatus),
                booking: _ctx.booking,
                class: "mt-auto"
              }, null, 8, ["isPastStatus", "booking"]))
            : _createCommentVNode("", true)
        ])
      ])
    ])
  ]))
}
}

})